import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";

/**
 * Compact date-range filter with quick presets.
 * Stateless — reads `from`/`to` (YYYY-MM-DD) and calls `onChange({ from, to })`.
 *
 * Layout: [Quick presets] [From input] [To input] [Clear]
 *
 * Quick presets:
 *   Today       → from=today, to=today
 *   Last 7 days → from=today-6, to=today
 *   Last 30 days→ from=today-29, to=today
 *   This Month  → from=1st-of-month, to=today
 */
function isoDate(d) { return d.toISOString().slice(0, 10); }
function todayISO() { return isoDate(new Date()); }
function daysAgoISO(n) { const d = new Date(); d.setDate(d.getDate() - n); return isoDate(d); }
function firstOfMonthISO() { const d = new Date(); d.setDate(1); return isoDate(d); }

const PRESETS = [
  { key: "today",   label: "Today",       calc: () => ({ from: todayISO(),       to: todayISO() }) },
  { key: "7d",      label: "Last 7 days", calc: () => ({ from: daysAgoISO(6),    to: todayISO() }) },
  { key: "30d",     label: "Last 30 days",calc: () => ({ from: daysAgoISO(29),   to: todayISO() }) },
  { key: "month",   label: "This Month",  calc: () => ({ from: firstOfMonthISO(),to: todayISO() }) },
];

export default function DateRangeFilter({ from, to, onChange, compact = false, className = "", testidPrefix = "daterange" }) {
  const set = (next) => onChange({ from: next.from || "", to: next.to || "" });
  const clear = () => onChange({ from: "", to: "" });

  // figure out which preset (if any) is currently active for visual highlight
  const activePreset = PRESETS.find((p) => {
    const v = p.calc();
    return v.from === from && v.to === to;
  })?.key;

  return (
    <div className={`flex flex-wrap items-end gap-2 ${className}`} data-testid={`${testidPrefix}-root`}>
      {PRESETS.map((p) => (
        <Button
          key={p.key}
          type="button"
          variant={activePreset === p.key ? "default" : "outline"}
          size="sm"
          className="h-9 text-xs"
          onClick={() => set(p.calc())}
          data-testid={`${testidPrefix}-preset-${p.key}`}
        >{p.label}</Button>
      ))}

      <div className="flex flex-col gap-1">
        {!compact && <Label className="text-[10.5px] uppercase tracking-wide text-slate-500">From</Label>}
        <Input
          type="date"
          value={from || ""}
          onChange={(e) => set({ from: e.target.value, to })}
          className="h-9 w-[148px] text-sm"
          data-testid={`${testidPrefix}-from`}
        />
      </div>
      <div className="flex flex-col gap-1">
        {!compact && <Label className="text-[10.5px] uppercase tracking-wide text-slate-500">To</Label>}
        <Input
          type="date"
          value={to || ""}
          onChange={(e) => set({ from, to: e.target.value })}
          className="h-9 w-[148px] text-sm"
          data-testid={`${testidPrefix}-to`}
        />
      </div>

      {(from || to) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 text-xs text-slate-500"
          onClick={clear}
          data-testid={`${testidPrefix}-clear`}
        ><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>
      )}
    </div>
  );
}
